import Logger from "@/utils/logger";

interface AgGridApi {
  setFocusedCell: (row: number, col: string) => void;
  startEditingCell: (params: { rowIndex: number; colKey: string }) => void;
}

interface AgGridElement extends Element {
  gridApi?: AgGridApi;
  __agGridInstance?: { api?: AgGridApi };
}

export function focusField(key: string) {
  // Handle special case for fee schedule grid cells
  if (key.startsWith('feeSchedule.')) {
    const match = key.match(/^feeSchedule\.(\d+)\.fee_amount$/);
    if (match) {
      const rowIndex = parseInt(match[1], 10);

      // Find the AG Grid API and focus the specific cell
      const gridElement = document.querySelector('.ag-theme-avpro') as AgGridElement | null;
      if (gridElement) {
        // Try to find the grid API instance
        const gridApi = gridElement.gridApi || gridElement.__agGridInstance?.api;
        if (gridApi) {
          try {
            gridApi.setFocusedCell(rowIndex, 'fee_amount');
            gridApi.startEditingCell({
              rowIndex: rowIndex,
              colKey: 'fee_amount'
            });
            return;
          } catch (error) {
            // Silently handle grid focus errors
          }
        }
      }
    }
  }

  // Handle day rate settings fields (minimumBillableHours, halfDayHours)
  // These may be inside a collapsed accordion that needs expanding first
  if (key === 'minimumBillableHours' || key === 'halfDayHours') {
    const input = document.getElementById(key);
    const isHidden = input?.closest('[aria-hidden="true"]');

    if (input && !isHidden) {
      input.focus({ preventScroll: true });
      // Delay scroll so virtual keyboard / layout shift settles first
      setTimeout(() => {
        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 120);
      return;
    }

    // Element is hidden or not in DOM — expand the collapsed Day Rate Settings section
    const collapseToggle = document.querySelector<HTMLButtonElement>(
      'button[aria-label="Toggle day rate settings"][aria-expanded="false"]'
    );
    if (collapseToggle) {
      collapseToggle.click();
      // Wait for AnimatedCollapse to open, then focus
      setTimeout(() => {
        const el = document.getElementById(key);
        if (el) {
          el.focus({ preventScroll: true });
          setTimeout(() => {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 120);
        }
      }, 400);
      return;
    }
  }

  // 1️⃣ locate the element (data-attr first, then id)
  const root =
    (document.querySelector(`[data-validation-key="${key}"]`) as HTMLElement | null) ??
    document.getElementById(key);

  if (!root) {
    return;
  }

  // 2️⃣ if the root isn't focusable (NumberInput wrappers are <div>s),
  //    grab the first focusable control inside it
  const target: HTMLElement =
    (root.matches("input,textarea,select,[tabindex]") ? root
      : root.querySelector<HTMLElement>("input,textarea,select,[tabindex]:not([tabindex='-1'])")) ||
    root; // fall back to root if nothing found

  // 3️⃣ skip if an ancestor has aria-hidden (e.g., Radix sheet still closing)
  if (target.closest('[aria-hidden="true"]')) return;

  // 4️⃣ deselect any existing input first
  (document.activeElement as HTMLElement | null)?.blur();

  // 5️⃣ scroll & focus
  target.focus({ preventScroll: true });
  target.scrollIntoView({ behavior: "smooth", block: "center" });
}

export function scrollToElement(id: string): void {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
  } else {
    Logger.warn(`scrollToElement: element with id '${id}' not found`, { component: 'navigation' });
  }
}
